import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Play, ChevronDown, Loader2, ListMusic, RefreshCw, Trash2 } from 'lucide-react';

import PublicLayout from '../../components/public/PublicLayout';
import { authSelector } from '../../features/auth/slice';
import SEO from '../../components/SEO';
import { PUBLIC_BASE } from './config';

const FALLBACK_IMG =
  'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/none_image.jpg';

const PROGRESS_STEPS = [
  { label: 'AI 큐레이션', duration: 30 },
  { label: 'YouTube 검색', duration: 120 },
  { label: '커버 이미지 생성', duration: 20 },
  { label: '저장 중', duration: 10 },
];
const TOTAL_ESTIMATED = PROGRESS_STEPS.reduce((sum, s) => sum + s.duration, 0);

function getMondayDate() {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d.toISOString().split('T')[0];
}

function ProgressBar({ elapsed }) {
  const progress = Math.min((elapsed / TOTAL_ESTIMATED) * 100, 95);

  let currentStep = 0;
  let accumulated = 0;
  for (let i = 0; i < PROGRESS_STEPS.length; i++) {
    accumulated += PROGRESS_STEPS[i].duration;
    if (elapsed < accumulated) { currentStep = i; break; }
    if (i === PROGRESS_STEPS.length - 1) currentStep = i;
  }

  return (
    <div className="w-full max-w-sm mt-6">
      <div className="flex justify-between text-[11px] text-slate-500 mb-2">
        <span>{PROGRESS_STEPS[currentStep].label}...</span>
        <span>{Math.floor(elapsed / 60)}:{String(Math.floor(elapsed % 60)).padStart(2, '0')} 경과</span>
      </div>
      <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-sky-500 to-blue-500 rounded-full transition-all duration-1000 ease-linear"
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className="flex justify-between mt-2 gap-1">
        {PROGRESS_STEPS.map((step, i) => (
          <div key={step.label} className="flex-1 text-center">
            <div className={`h-0.5 rounded-full mb-1 ${i <= currentStep ? 'bg-sky-500/40' : 'bg-white/5'}`} />
            <span className={`text-[9px] ${i <= currentStep ? 'text-sky-400/70' : 'text-slate-600'}`}>
              {step.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PlaylistCard({ playlist, isNew, isAdmin, onDelete }) {
  const navigate = useNavigate();

  const handleDelete = (e) => {
    e.stopPropagation();
    if (window.confirm(`"${playlist.name}" 플레이리스트를 삭제하시겠습니까?`)) {
      onDelete(playlist.id);
    }
  };

  const goDetail = () => navigate(`${PUBLIC_BASE}/playlists/${playlist.id}`);

  return (
    <div
      onClick={goDetail}
      className="group/card bg-[#182334] rounded-2xl overflow-hidden border border-white/5 cursor-pointer hover:border-white/15 transition-all hover:shadow-xl hover:shadow-black/20">
      {/* 커버 이미지 (16:9) */}
      <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
        <img
          src={playlist.coverImage || FALLBACK_IMG}
          alt={playlist.name}
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* 재생 오버레이 */}
        <button
          onClick={(e) => { e.stopPropagation(); goDetail(); }}
          className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/40 transition-colors group">
          <div className="w-14 h-14 rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 bg-sky-500 hover:bg-sky-400">
            <Play size={22} fill="currentColor" className="text-white ml-1" />
          </div>
        </button>
        {isNew && (
          <span className="absolute top-3 right-3 bg-sky-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wide shadow-lg">
            NEW
          </span>
        )}
        {isAdmin && (
          <button
            onClick={handleDelete}
            className="absolute top-3 left-3 p-1.5 bg-black/50 hover:bg-red-500/80 rounded-lg opacity-0 group-hover/card:opacity-100 transition-all"
            title="삭제">
            <Trash2 size={14} className="text-white" />
          </button>
        )}
      </div>

      {/* 정보 영역 */}
      <div className="p-4">
        <span className="inline-block text-xs text-sky-400 bg-sky-500/10 px-2 py-0.5 rounded-full mb-2">
          {playlist.concept}
        </span>
        <h3 className="text-white font-bold text-base mb-1 leading-snug group-hover/card:text-sky-300 transition-colors">{playlist.name}</h3>
        <p className="text-slate-400 text-xs leading-relaxed mb-1.5 line-clamp-2">
          {playlist.description}
        </p>
        {(() => {
          const artists = [...new Set((playlist.songs || []).map((s) => s.artistKr).filter(Boolean))];
          const display = artists.slice(0, 3);
          return display.length > 0 ? (
            <p className="text-sky-400/70 text-xs mb-1.5 truncate">
              {display.join(', ')}{artists.length > 3 ? ' 외' : ''}
            </p>
          ) : null;
        })()}
        <p className="text-slate-600 text-xs mb-4">
          {playlist.songs?.length}곡
          {(() => {
            const total = (playlist.songs || []).reduce((sum, s) => sum + (s.duration || 0), 0);
            if (total <= 0) return '';
            if (total >= 3600) return ` · ${Math.floor(total / 3600)}시간 ${Math.round((total % 3600) / 60)}분`;
            return ` · ${Math.round(total / 60)}분`;
          })()}
        </p>

        {/* 버튼 */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={(e) => { e.stopPropagation(); goDetail(); }}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap bg-sky-500 hover:bg-sky-400 text-white">
            <Play size={11} fill="currentColor" />
            재생
          </button>
        </div>
      </div>
    </div>
  );
}

function PublicPlaylistPage() {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef(null);
  const { me } = useSelector(authSelector.selectAuthState);
  const isAdmin = me?.adminYn === 'Y';
  const currentMonday = getMondayDate();

  const fetchPlaylists = useCallback((offset = 0, limit = 6) => {
    return axios.get('/public/playlists', { params: { offset, limit } }).then((res) => res.data);
  }, []);

  const startTimer = useCallback(() => {
    setElapsed(0);
    timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopTimer();
  }, [stopTimer]);

  useEffect(() => {
    fetchPlaylists()
      .then((data) => {
        if (data.items?.length === 0) {
          setGenerating(true);
          startTimer();
          const timer = setTimeout(() => {
            setGenerating(false);
            stopTimer();
            fetchPlaylists().then((d) => {
              setPlaylists(d.items || []);
              setHasMore(d.hasMore || false);
            }).catch(() => {});
          }, 30000);
          return () => clearTimeout(timer);
        }
        setPlaylists(data.items || []);
        setHasMore(data.hasMore || false);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [fetchPlaylists, startTimer, stopTimer]);

  const handleLoadMore = () => {
    setLoadingMore(true);
    fetchPlaylists(playlists.length, 6)
      .then((data) => {
        setPlaylists((prev) => [...prev, ...(data.items || [])]);
        setHasMore(data.hasMore || false);
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/public/playlists/${id}`);
      setPlaylists((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      alert('삭제에 실패했습니다.');
    }
  };

  const handleRegenerate = async () => {
    if (!window.confirm('플레이리스트 3개를 새로 생성하시겠습니까?\n(YouTube 검색 포함, 약 2~3분 소요)')) return;
    setGenerating(true);
    startTimer();
    try {
      const current = await fetchPlaylists(0, 1);
      const initialTotal = current.total || 0;

      await axios.post('/public/playlists/regenerate');

      let pollElapsed = 0;
      const poll = setInterval(() => {
        pollElapsed += 10000;
        fetchPlaylists()
          .then((data) => {
            if ((data.total || 0) > initialTotal) {
              setPlaylists(data.items || []);
              setHasMore(data.hasMore || false);
              setGenerating(false);
              stopTimer();
              clearInterval(poll);
            } else if (pollElapsed >= 300000) {
              setGenerating(false);
              stopTimer();
              clearInterval(poll);
            }
          })
          .catch(() => {});
      }, 10000);
    } catch (err) {
      console.error('[재생성] 실패:', err);
      setGenerating(false);
      stopTimer();
    }
  };

  return (
    <PublicLayout>
      <SEO
        title="플레이리스트"
        description="AI가 큐레이션한 주간 플레이리스트를 들어보세요."
        path="/playlists"
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-10">
        <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight text-center mb-2">플레이리스트</h1>
        <div className="flex items-center justify-center gap-3 mb-1">
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-sky-500/10 border border-sky-500/20 text-sky-400 text-xs font-medium">
            {(() => { const now = new Date(); return `${now.getMonth() + 1}월 ${Math.ceil(now.getDate() / 7)}주차`; })()}
          </span>
        </div>
        <p className="text-slate-500 text-sm text-center mb-8">매주 새로운 테마로 큐레이션된 플레이리스트</p>
        <div className="flex items-end justify-end mb-8">
          {isAdmin && !generating && (
            <button
              onClick={handleRegenerate}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-400 hover:text-sky-400 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors">
              <RefreshCw size={12} />
              재생성
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 gap-3 text-slate-400">
            <Loader2 size={20} className="animate-spin" />
            <span>로딩 중...</span>
          </div>
        ) : generating ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4 text-slate-400">
            <div className="w-12 h-12 rounded-full border-2 border-sky-500 border-t-transparent animate-spin" />
            <p className="text-sm">플레이리스트를 생성하고 있습니다...</p>
            <p className="text-xs text-slate-600">AI 큐레이션 + YouTube 검색 + 커버 이미지 생성, 약 2~3분 소요됩니다</p>
            <ProgressBar elapsed={elapsed} />
          </div>
        ) : playlists.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-slate-500">
            <ListMusic size={40} />
            <p>플레이리스트가 없습니다.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 items-start">
              {playlists.filter((p) => p.youtubeVideoId).map((playlist) => (
                <PlaylistCard
                  key={playlist.id}
                  playlist={playlist}
                  isNew={playlist.weekOf === currentMonday}
                  isAdmin={isAdmin}
                  onDelete={handleDelete}
                />
              ))}
            </div>

            {/* 더보기 */}
            {hasMore && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="flex items-center gap-2 px-6 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-slate-300 text-sm transition-colors disabled:opacity-50">
                  {loadingMore ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <ChevronDown size={14} />
                  )}
                  지난 플레이리스트 더보기
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </PublicLayout>
  );
}

export default PublicPlaylistPage;
